/**
 * Die zwei Achsen einer Reise: **Antrieb** und **Übernachtung**.
 *
 * Vier Kombinationen, eine Oberfläche. Damit die nicht zu vier Oberflächen
 * wird, steht hier an einer Stelle, was die Achsen bedeuten — die Vorgaben,
 * die Sätze, mit denen sie erklärt werden, und die wenigen Ableitungen, die
 * mehr als eine Ansicht braucht.
 *
 * ## Warum ein eigenes Modul und nicht `planner/`
 *
 * Der Rechenkern läuft auch auf dem iPhone und kennt nur Zahlen: Stationen,
 * Etappen, Ladekurven. Die Reiseart entscheidet nicht, *wie* gerechnet wird,
 * sondern *ob* gerechnet wird — bei einer Verbrenner-Reise wird der Ladeplaner
 * schlicht nicht aufgerufen. Das ist eine Frage der Oberfläche und gehört
 * deshalb neben sie und nicht in den Kern.
 *
 * ## Warum fehlende Werte elektro und fahrzeug bedeuten
 *
 * Genau wie im Backend (`ist_verbrenner`): Die Spalten haben eine Vorgabe, und
 * eine Reise aus der Zeit davor bringt die Felder womöglich gar nicht mit. Sie
 * dann als Verbrenner-Reise zu behandeln hieße, bestehenden Reisen still die
 * Ladeplanung wegzunehmen.
 *
 * ## Was hier bewusst nicht steht
 *
 * Eine Tankplanung als Gegenstück zur Ladeplanung. Ein Verbrenner mit 700 km
 * Reichweite hat kein Reichweitenproblem, das eine Ansicht verdient — die
 * richtige Antwort ist ein Reiter weniger, nicht ein anderer Reiter.
 */

import { KATEGORIEN } from './schlafplatz';

export type Antrieb = 'elektro' | 'verbrenner';
export type Uebernachtung = 'fahrzeug' | 'unterkunft' | 'gemischt';

export const ANTRIEB_VORGABE: Antrieb = 'elektro';
export const UEBERNACHTUNG_VORGABE: Uebernachtung = 'fahrzeug';

/** Alle Werte der Übernachtungsachse — die eine Wahrheit für die Rückfallprüfung. */
const UEBERNACHTUNGEN: readonly Uebernachtung[] = ['fahrzeug', 'unterkunft', 'gemischt'];

/** Eine Antwort auf eine der beiden Fragen — Knopfaufschrift und Folgesatz. */
export interface Wahl<T> {
  wert: T;
  label: string;
  /**
   * Was sich dadurch ändert, in einem Satz.
   *
   * Er steht beim Anlegen unter der Auswahl und in den Reiseeinstellungen
   * unter derselben Zeile. Beide Male beantwortet er dieselbe Frage — „was
   * passiert, wenn ich das nehme?" —, deshalb steht er einmal hier und nicht
   * zweimal in zwei Vorlagen.
   */
  folge: string;
}

export const ANTRIEB_WAHL: Wahl<Antrieb>[] = [
  {
    wert: 'elektro',
    label: 'Elektro',
    folge:
      'Mit Ladeplanung: eine Ladekurve über die ganze Reise, Ladestopps und die Frage, ob du ankommst.'
  },
  {
    wert: 'verbrenner',
    label: 'Verbrenner',
    folge:
      'Ohne Ladeplanung — der Reiter Laden entfällt. Bei 700 km Reichweite gibt es kein Reichweitenproblem, das eine Ansicht verdient.'
  }
];

export const UEBERNACHTUNG_WAHL: Wahl<Uebernachtung>[] = [
  {
    wert: 'fahrzeug',
    label: 'Im Fahrzeug',
    folge:
      'Stellplätze, Campingplätze und Hotels gleichrangig — bewertet nach Sanitär, Untergrund, Gebühr und ob man dort stehen darf.'
  },
  {
    wert: 'gemischt',
    label: 'Gemischt',
    /*
     * Steht **zwischen** den beiden anderen, obwohl es in der Datenbank hinten
     * im ENUM hängt (MariaDB legt Werte als Index ab, siehe `db._ENUM_WERTE`).
     * Die Achse ist ein Verlauf, kein Nachtrag: überwiegend Fahrzeug, ganz
     * Unterkunft, und dazwischen der häufigste Fall.
     *
     * Der letzte Satz nennt den Weg, der wirklich existiert — und das ist
     * seit dem Hotelnacht-Bündel ein anderer: Der Schlafreiter trägt je
     * Stationsnacht einen Schalter, der `sleep_kind` setzt, und der Kern
     * rechnet die Nacht dann ohne das Auto. Vorher nannte der Satz den Umweg
     * über die Auswahl eines konkreten Hotels; der geht weiterhin, ist aber
     * nicht mehr der Weg, den man jemandem zeigt.
     */
    folge:
      'Stellplätze, Campingplätze und Unterkünfte nebeneinander — jede Art nach ihren eigenen Maßstäben bewertet. Packliste und Ladefrage bleiben vollständig. Für eine einzelne Hotelnacht stellst du im Schlafreiter den Schalter dieser Nacht auf Unterkunft — der Plan rechnet sie dann ohne das Auto.'
  },
  {
    wert: 'unterkunft',
    label: 'Unterkunft',
    /*
     * Der Entwurf verspricht hier „Preis, Buchungsstand und Check-in-Fenster".
     * Das gibt die Datenlage nicht her: Gesucht wird in OpenStreetMap, und
     * dort stehen für ein Hotel weder Preise noch freie Zimmer noch
     * Check-in-Zeiten — `schlafplatz.ts` sagt das selbst („Preise und
     * Bewertungen nur über die Website"). Ein Satz, der drei Angaben ankündigt,
     * die dann nirgends stehen, ist schlechter als einer, der weniger
     * verspricht: Ehrlichkeit vor Vollständigkeit.
     */
    folge:
      'Gesucht werden Hotels und Pensionen; Sanitär, Untergrund und Stellplatzgebühr fallen aus der Bewertung. Bei einem Elektroauto kommt die Frage dazu, ob das Quartier über Nacht lädt.'
  }
];

/**
 * Der Satz zur Kombination Elektro + Unterkunft.
 *
 * Sie erzeugt eine Frage, die es sonst nirgends gibt: Eine Unterkunft mit
 * Wallbox ersetzt einen Ladestopp am nächsten Tag. Deshalb steht der Hinweis
 * beim Anlegen — und deshalb steht die Ladefrage im Quartier **vor** dem
 * Preis, nicht in einer Merkmalskette hinter Dusche und WLAN.
 *
 * Er gilt genauso für Elektro + gemischt: Auch dort gibt es Unterkunftsnächte.
 * Der Titel nennt trotzdem beide Enden der Achse und nicht die gewählte
 * Antwort — er benennt die Kombination, um die es geht, und das ist in beiden
 * Fällen dieselbe.
 */
export const ELEKTRO_UNTERKUNFT_TITEL = 'Elektro und Unterkunft';
export const ELEKTRO_UNTERKUNFT_HINWEIS =
  'Dann wird am Quartier wichtig, ob es über Nacht lädt. Eine Unterkunft mit Wallbox ersetzt einen Ladestopp am nächsten Tag.';

/** Die Frage, die über der jeweiligen Zeile steht. */
export const ANTRIEB_FRAGE = 'Womit fährst du?';
export const UEBERNACHTUNG_FRAGE = 'Wo schläfst du?';

/** Was eine Reise über sich sagt — mehr braucht hier niemand. */
type Reiseart = { antrieb?: Antrieb | null; uebernachtung?: Uebernachtung | null } | null | undefined;

export function antriebVon(reise: Reiseart): Antrieb {
  return reise?.antrieb === 'verbrenner' ? 'verbrenner' : ANTRIEB_VORGABE;
}

export function uebernachtungVon(reise: Reiseart): Uebernachtung {
  // Gegen die Werteliste geprüft und nicht gegen einen einzelnen Wert: Mit
  // drei Möglichkeiten wäre die alte Kette aus Vergleichen der Ort, an dem der
  // vierte Wert eines Tages still zur Vorgabe würde. Was nicht in der Liste
  // steht — „zelt" aus einem alten Schnappschuss — bleibt die Vorgabe.
  const u = reise?.uebernachtung;
  return u && UEBERNACHTUNGEN.includes(u) ? u : UEBERNACHTUNG_VORGABE;
}

/** Gibt es an dieser Reise überhaupt eine Energiefrage? */
export function istElektro(reise: Reiseart): boolean {
  return antriebVon(reise) === 'elektro';
}

/** Ist das eine reine Fahrzeugreise? Streng — `gemischt` ist es nicht. */
export function imFahrzeug(reise: Reiseart): boolean {
  return uebernachtungVon(reise) === 'fahrzeug';
}

/**
 * Die zwei Fragen, in die `gemischt` die Achse zerlegt.
 *
 * Bis hierher war „im Fahrzeug schlafen" **eine** Eigenschaft der Reise, und
 * jede Verzweigung fragte sie mit demselben Vergleich ab. Mit der dritten
 * Antwort geht das nicht mehr: Eine gemischte Reise hat Fahrzeugnächte **und**
 * Unterkunftsnächte, und die beiden Welten hängen an verschiedenen Stellen der
 * Oberfläche. Die Packliste fragt nach der einen, die Ladefrage am Quartier
 * nach der anderen — mit einem einzigen Vergleich fiele bei `gemischt`
 * zwangsläufig eine von beiden weg.
 *
 * Zwei benannte Funktionen statt zweier Vergleiche in zwei Vorlagen: So steht
 * in jeder Verzweigung, **welche** der beiden Fragen sie stellt, und beide
 * sind ohne Browser prüfbar.
 */
export function fahrzeugnaechte(u: Uebernachtung): boolean {
  return u !== 'unterkunft';
}

export function unterkunftsnaechte(u: Uebernachtung): boolean {
  return u !== 'fahrzeug';
}

/**
 * Wo wird geschlafen, wenn die einzelne Nacht nichts dazu sagt?
 *
 * Die Vorgabe, die die Oberfläche in den Rechenkern hineinreicht
 * (`AchseKontext.im_auto_vorgabe`). Sie steht hier und nicht dort, weil der
 * Kern die Reiseart nicht kennen soll — er rechnet, sie entscheidet nur, *ob*
 * gerechnet wird und womit.
 *
 * `gemischt` fällt auf **Fahrzeug** zurück und nicht auf Unterkunft: Eine
 * gemischte Reise ist überwiegend eine Fahrzeugreise mit einzelnen
 * Hotelnächten, und die Hotelnacht ist die Angabe, die man ausdrücklich macht.
 * Andersherum müsste man jede Fahrzeugnacht einzeln bestätigen — und die
 * vergessene Bestätigung wäre der teure Fehler: zu wenig Ladung eingeplant.
 *
 * Getrennt von `fahrzeugnaechte` gehalten, obwohl beide heute dasselbe
 * ausrechnen: Die eine fragt „gibt es solche Nächte überhaupt?" (Packliste),
 * die andere „was gilt, wenn niemand etwas sagt?" (Rechenkern). Zwei Fragen,
 * die auseinandergehen können — etwa wenn `gemischt` eines Tages eine eigene
 * Vorgabe bekommt.
 */
export function imFahrzeugVorgabe(u: Uebernachtung): boolean {
  return u !== 'unterkunft';
}

/**
 * Wird am Quartier nach der Wallbox gefragt?
 *
 * Nur wo beides zusammenkommt: ein Elektroauto **und** Nächte in einer
 * Unterkunft. Bei einer Verbrenner-Reise gibt es nichts zu laden, bei einer
 * reinen Fahrzeugreise kein Quartier, das es täte — bei `gemischt` dagegen
 * schon, und genau das ist der Grund, warum diese Weiche nicht mehr am
 * Paneltitel hängen darf: Der sagt bei `gemischt` „Schlafen", die Ladefrage
 * gehört trotzdem dazu.
 */
export function ladefrageAmQuartier(antrieb: Antrieb, u: Uebernachtung): boolean {
  return antrieb === 'elektro' && unterkunftsnaechte(u);
}

/**
 * Gehört dieser Reiter zu dieser Reise?
 *
 * Die eine Stelle, an der die Achse über eine ganze Fläche entscheidet.
 * Deshalb steht die Regel als Funktion und nicht als Bedingung in der
 * Vorlage — so lässt sie sich prüfen.
 *
 * **Was hier nicht mehr mit entschieden wird:** ob der Ladeplaner läuft. Bis
 * zu dieser Etappe war das eine Nebenwirkung — ohne Reiter kein Panel, ohne
 * Panel keine Rechnung und keine Säulenabfrage. Genau das war der Fehler: Wer
 * den Reiter nie öffnete, hatte keinen Ladeplan, auch nicht im Ausdruck und
 * nicht im Schnappschuss. Gerechnet wird jetzt im Planer, und zwar an
 * derselben Achse (`antriebVon(...) === 'elektro'`), aber unabhängig davon,
 * was gerade angezeigt wird.
 *
 * Es ist bewusst nur **ein** Reiter. Wer hier anfängt, „Vorbereitung" oder
 * „Entdecken" je Achse auszudünnen, baut vier Oberflächen.
 */
export function reiterSichtbar(key: string, antrieb: Antrieb): boolean {
  return key !== 'laden' || antrieb === 'elektro';
}

/**
 * Welche Schlafplatz-Kategorien gesucht werden.
 *
 * Bei Unterkunft schrumpfen die drei gleichrangigen Kategorien auf eine. Das
 * ist mehr als eine Filterzeile: Wer im Hotel schläft, bekommt keine zwei
 * leeren Überschriften „Campingplatz" und „Stellplatz" zu sehen, sondern eine
 * Liste ohne Gruppierung — die Gruppierung trägt erst ab zwei Gruppen etwas.
 *
 * `gemischt` bekommt alle drei, und zwar aus demselben Grund, aus dem
 * `fahrzeug` sie bekommt: Es gibt Nächte für jede von ihnen. Der Unterschied
 * zwischen den beiden liegt nicht in der Liste, sondern im **Maßstab** je
 * Kategorie — der steht in `schlafplatz.ts`.
 */
export function schlafkategorien(u: Uebernachtung): (typeof KATEGORIEN)[number][] {
  return u === 'unterkunft' ? KATEGORIEN.filter((k) => k.key === 'hotel') : [...KATEGORIEN];
}

/** Die Antwort an einer einzelnen Nacht — `reise` heißt „keine Abweichung". */
export type Nachtwahl = 'reise' | 'fahrzeug' | 'unterkunft';

/**
 * Welche Übernachtungsart für **diese eine Nacht** gilt.
 *
 * Die Reiseart setzt die Vorgabe, die einzelne Nacht darf abweichen: zehn
 * Nächte im Fahrzeug, zwei im Hotel ist eine echte Reise und keine
 * Sonderlocke. Bisher schrieb der Schalter „Wo schläfst du hier?" zwar
 * `sleep_kind` und der Ladeplaner rechnete danach — die **Suche** darunter
 * blieb aber an der Reise hängen. Wer an einer Fahrzeugreise „in einer
 * Unterkunft" wählte, bekam für diese Nacht weiter nur Stell- und
 * Campingplätze angeboten. Der Schalter versprach damit etwas, das die Liste
 * eine Zeile tiefer nicht einlöste.
 *
 * Die Auflösung braucht keinen vierten Zustand: Eine abweichende Nacht **ist**
 * eine gemischte Nacht. `gemischt` bringt schon alles mit, was sie braucht —
 * alle drei Kategorien in der Suche (`schlafkategorien`) und den Maßstab je
 * Treffer statt je Reise (`schlafplatz.fahrzeugmassstab`). Beides ist gebaut
 * und geprüft; hier wird es nur an der richtigen Stelle angefragt.
 *
 * Bewusst **erweiternd und nie verengend**: Die Abweichung legt Kategorien
 * dazu, sie nimmt keine weg. Das ist wichtig, weil `sleep_kind` zwei Dinge
 * trägt (die Ortskategorie eines übernommenen Platzes **und** die
 * ausdrückliche Ansage, siehe `api.ts`). Würde die Antwort verengen, verlöre
 * jemand, der einen Campingplatz übernommen hat, mit dem Übernehmen die
 * Möglichkeit, es sich anders zu überlegen — die Liste wäre nach der Wahl
 * schmaler als davor. So kann kein Fall etwas verlieren, und wo die Reise die
 * Kategorie ohnehin schon führt, ändert sich gar nichts.
 */
export function nachtUebernachtung(reise: Uebernachtung, wahl: Nachtwahl): Uebernachtung {
  if (wahl === 'reise') return reise;
  if (wahl === 'fahrzeug') return reise === 'fahrzeug' ? 'fahrzeug' : 'gemischt';
  return reise === 'unterkunft' ? 'unterkunft' : 'gemischt';
}

/**
 * Der Satz, der die ZTL-Regel bei einer Verbrenner-Reise ersetzt.
 *
 * Die Regeltabelle in `planner/ztl.ts` ist für ein E-Auto geschrieben — dort
 * steht, wo Elektrofahrzeuge befreit sind und wo die Anmeldung dazu nötig ist.
 * Für eine Verbrenner-Reise ist dieser Satz im besten Fall unbrauchbar und im
 * schlechtesten falsch: „Diesel-Fahrverbote betreffen dich nicht" gilt dann
 * nicht mehr, und ein Bußgeld kommt erst Monate später.
 *
 * Deshalb wird die Regel nicht umgeschrieben, sondern weggelassen und durch
 * den Verweis ersetzt, der ohnehin daneben steht. Ein Satz, der sich zur
 * eigenen Reise nicht äußert, ist besser als einer, der etwas Falsches sagt.
 */
export const ZTL_OHNE_REGEL =
  'Welche Ausnahmen für dein Fahrzeug gelten, steht in der verbindlichen Auskunft der Kommune.';

/**
 * Der ZTL-Hinweistext, der zu diesem Antrieb passt.
 *
 * `text` kommt aus `ztl.ts` und trägt bei fehlender Genehmigung die Regel der
 * Stadt am Ende. Sie wird hier abgetrennt statt neu formuliert: Die Tabelle
 * bleibt unangetastet, und ein Text, den zwei Stellen unabhängig voneinander
 * zusammenbauen, läuft nicht auseinander.
 */
export function ztlText(
  befund: { text: string; regel?: string | null },
  antrieb: Antrieb
): string {
  if (antrieb === 'elektro') return befund.text;
  const regel = befund.regel?.trim();
  const roh = befund.text.trim();
  const rumpf = regel && roh.endsWith(regel) ? roh.slice(0, -regel.length).trim() : roh;
  return rumpf ? `${rumpf} ${ZTL_OHNE_REGEL}` : ZTL_OHNE_REGEL;
}
